import { useState, useEffect, useCallback } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

// ─── Competiciones ────────────────────────────────────────────────────────────
const COMPETITIONS = [
  { id: 'laliga',     label: 'LaLiga',           country: 'España',     flag: '🇪🇸', espnId: 'esp.1',           sport: 'soccer',     group: 'ligas',    isKnockout: false },
  { id: 'premier',    label: 'Premier League',   country: 'Inglaterra', flag: '🏴󠁧󠁢󠁥󠁮󠁧󠁿', espnId: 'eng.1',           sport: 'soccer',     group: 'ligas',    isKnockout: false },
  { id: 'bundesliga', label: 'Bundesliga',        country: 'Alemania',   flag: '🇩🇪', espnId: 'ger.1',           sport: 'soccer',     group: 'ligas',    isKnockout: false },
  { id: 'seriea',     label: 'Serie A',           country: 'Italia',     flag: '🇮🇹', espnId: 'ita.1',           sport: 'soccer',     group: 'ligas',    isKnockout: false },
  { id: 'ligue1',     label: 'Ligue 1',           country: 'Francia',    flag: '🇫🇷', espnId: 'fra.1',           sport: 'soccer',     group: 'ligas',    isKnockout: false },
  { id: 'champions',  label: 'Champions League',  country: 'UEFA',       flag: '⭐', espnId: 'uefa.champions',  sport: 'soccer',     group: 'europa',   isKnockout: true  },
  { id: 'europa',     label: 'Europa League',     country: 'UEFA',       flag: '🏆', espnId: 'uefa.europa',     sport: 'soccer',     group: 'europa',   isKnockout: true  },
  { id: 'conference', label: 'Conference League', country: 'UEFA',       flag: '🌿', espnId: 'uefa.europa.conf',sport: 'soccer',     group: 'europa',   isKnockout: true  },
  { id: 'nba',        label: 'NBA',               country: 'EE.UU.',     flag: '🏀', espnId: 'nba',             sport: 'basketball', group: 'basket',   isKnockout: false },
] as const;

type CompId = typeof COMPETITIONS[number]['id'];

const GROUPS = [
  { id: 'ligas',  label: 'Grandes Ligas',  icon: 'ri-football-line' },
  { id: 'europa', label: 'Europa — Clubes', icon: 'ri-award-line' },
  { id: 'basket', label: 'Baloncesto',      icon: 'ri-basketball-line' },
] as const;

// ─── Tipos ────────────────────────────────────────────────────────────────────
interface StandingTeam {
  position: number;
  team: string;
  shortName: string;
  crest: string;
  played: number;
  won: number;
  draw: number;
  lost: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDiff: number;
  points: number;
  form: ('W' | 'D' | 'L')[];
  pct?: number;
}

interface KnockoutMatch {
  id: string;
  homeTeam: string;
  awayTeam: string;
  homeCrest: string;
  awayCrest: string;
  homeScore: string;
  awayScore: string;
  date: string;
  time: string;
  status: 'pre' | 'in' | 'post';
  round: string;
  venue: string;
}

interface UpcomingMatch {
  id: string;
  homeTeam: string;
  awayTeam: string;
  homeCrest: string;
  awayCrest: string;
  date: string;
  time: string;
  venue: string;
  status: 'pre' | 'in' | 'post';
  homeScore: string;
  awayScore: string;
}

interface OddsEvent {
  id: string;
  homeTeam: string;
  awayTeam: string;
  date: string;
  time: string;
  homeOdds: Record<string, number>;
  drawOdds: Record<string, number>;
  awayOdds: Record<string, number>;
  overOdds: Record<string, number>;
  underOdds: Record<string, number>;
  totalsLine: number;
  bookmakers: string[];
}

// ─── Utilidades ───────────────────────────────────────────────────────────────
function getStat(stats: Record<string, unknown>[], name: string): number {
  const found = stats?.find((s) => s.name === name || s.abbreviation === name.toUpperCase());
  return found ? (parseFloat(String(found.value)) || 0) : 0;
}

function parseForm(formStr: string): ('W' | 'D' | 'L')[] {
  return (formStr || '').toUpperCase().replace(/[^WDL]/g, '').split('').slice(-5) as ('W' | 'D' | 'L')[];
}

function getBest(oddsObj: Record<string, number>): number {
  const vals = Object.values(oddsObj).filter((v) => v > 0);
  return vals.length > 0 ? Math.max(...vals) : 0;
}

// ─── Sub-componentes ──────────────────────────────────────────────────────────
function TeamCrest({ src, alt, size = 7 }: { src: string; alt: string; size?: number }) {
  const [err, setErr] = useState(false);
  const cls = `w-${size} h-${size}`;
  if (!src || err) {
    return (
      <div className={`${cls} rounded-full bg-[#0d0f14] border border-[#2a2d3a] flex items-center justify-center flex-shrink-0`}>
        <i className="ri-shield-line text-[#3a3d4a] text-xs"></i>
      </div>
    );
  }
  return (
    <div className={`${cls} rounded-full bg-[#0d0f14] border border-[#2a2d3a] overflow-hidden flex items-center justify-center flex-shrink-0`}>
      <img src={src} alt={alt} className="w-full h-full object-contain p-0.5" onError={() => setErr(true)} />
    </div>
  );
}

function FormBadge({ result }: { result: 'W' | 'D' | 'L' }) {
  const styles: Record<string, string> = {
    W: 'bg-[#00e87a]/20 text-[#00e87a] border border-[#00e87a]/30',
    D: 'bg-[#f59e0b]/20 text-[#f59e0b] border border-[#f59e0b]/30',
    L: 'bg-[#ef4444]/20 text-[#ef4444] border border-[#ef4444]/30',
  };
  return <span className={`w-5 h-5 flex items-center justify-center rounded text-[10px] font-black ${styles[result]}`}>{result}</span>;
}

function SkeletonRow() {
  return (
    <div className="flex items-center gap-3 px-5 py-3 border-b border-[#1e2028] animate-pulse">
      <div className="w-5 h-4 bg-[#2a2d3a] rounded"></div>
      <div className="w-8 h-8 bg-[#2a2d3a] rounded-full"></div>
      <div className="flex-1 h-4 bg-[#2a2d3a] rounded"></div>
      <div className="w-8 h-4 bg-[#2a2d3a] rounded ml-auto"></div>
      <div className="w-8 h-4 bg-[#2a2d3a] rounded"></div>
      <div className="w-10 h-4 bg-[#2a2d3a] rounded"></div>
    </div>
  );
}

// ─── Odds Match Card ──────────────────────────────────────────────────────────
function OddsMatchCard({ event, isSoccer }: { event: OddsEvent; isSoccer: boolean }) {
  const dateObj = event.date ? new Date(`${event.date}T12:00:00`) : null;
  const day = dateObj ? dateObj.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' }) : '—';

  const bestHome = getBest(event.homeOdds);
  const bestDraw = getBest(event.drawOdds);
  const bestAway = getBest(event.awayOdds);

  const topBMs = event.bookmakers.slice(0, 4);

  return (
    <div className="bg-[#151719] border border-[#2a2d3a] rounded-xl overflow-hidden hover:border-[#00e87a]/20 transition-colors">
      {/* Header */}
      <div className="px-4 py-3 border-b border-[#1e2028] flex items-center justify-between">
        <span className="text-[#ffd700] text-[10px] font-bold">{day} · {event.time}</span>
        <span className="flex items-center gap-1 text-[#00e87a] text-[10px] font-bold">
          <span className="w-1.5 h-1.5 rounded-full bg-[#00e87a] animate-pulse inline-block"></span>
          Cuotas reales
        </span>
      </div>

      {/* Teams */}
      <div className="px-4 py-3">
        <div className="flex items-center justify-between mb-3">
          <span className="text-white text-sm font-bold truncate flex-1">{event.homeTeam}</span>
          <span className="text-[#3d4149] text-xs font-black px-2">vs</span>
          <span className="text-white text-sm font-bold truncate flex-1 text-right">{event.awayTeam}</span>
        </div>

        {/* Best odds highlight */}
        <div className={`grid ${isSoccer ? 'grid-cols-3' : 'grid-cols-2'} gap-2 mb-3`}>
          {[
            { label: isSoccer ? '1' : event.homeTeam.split(' ').slice(-1)[0], val: bestHome, color: '#00e87a' },
            ...(isSoccer ? [{ label: 'X', val: bestDraw, color: '#f59e0b' }] : []),
            { label: isSoccer ? '2' : event.awayTeam.split(' ').slice(-1)[0], val: bestAway, color: '#00e87a' },
          ].map((item) => (
            <div key={item.label} className="bg-[#0d0f14] rounded-lg p-2 text-center border border-[#2a2d3a]">
              <p className="text-[#6b7280] text-[10px] mb-1">{item.label}</p>
              <p className="font-black text-base tabular-nums" style={{ color: item.color }}>
                {item.val > 0 ? item.val.toFixed(2) : '—'}
              </p>
              <p className="text-[#3d4149] text-[9px] mt-0.5">mejor cuota</p>
            </div>
          ))}
        </div>

        {/* Per-bookmaker breakdown */}
        {topBMs.length > 0 && (
          <div className="space-y-1">
            {topBMs.map((bm) => {
              const h = event.homeOdds[bm] ?? 0;
              const d = event.drawOdds[bm] ?? 0;
              const a = event.awayOdds[bm] ?? 0;
              if (h <= 0 && a <= 0) return null;
              return (
                <div key={bm} className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-[#0d0f14]/60">
                  <span className="text-[#6b7280] text-[10px] font-bold w-20 flex-shrink-0 truncate">{bm}</span>
                  <div className={`flex-1 grid ${isSoccer ? 'grid-cols-3' : 'grid-cols-2'} gap-1`}>
                    {[h, ...(isSoccer ? [d] : []), a].map((val, i) => (
                      <span key={i} className={`text-center text-xs font-bold tabular-nums ${val > 0 ? 'text-white' : 'text-[#3d4149]'}`}>
                        {val > 0 ? val.toFixed(2) : '—'}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
            {event.bookmakers.length > 4 && (
              <p className="text-[#3d4149] text-[10px] text-center pt-1">
                +{event.bookmakers.length - 4} casas más en el comparador
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Standings Table ──────────────────────────────────────────────────────────
function StandingsTable({ data, loading, isEuropean, isBasketball }: {
  data: StandingTeam[];
  loading: boolean;
  isEuropean?: boolean;
  isBasketball?: boolean;
}) {
  return (
    <div className="bg-[#151719] border border-[#2a2d3a] rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[580px]">
          <thead>
            <tr className="border-b border-[#2a2d3a] bg-[#0d0f14]/60">
              <th className="px-5 py-3 text-left text-[#6b7280] text-[10px] font-bold uppercase tracking-wider w-8">#</th>
              <th className="px-3 py-3 text-left text-[#6b7280] text-[10px] font-bold uppercase tracking-wider">Equipo</th>
              <th className="px-3 py-3 text-center text-[#6b7280] text-[10px] font-bold uppercase tracking-wider">PJ</th>
              <th className="px-3 py-3 text-center text-[#6b7280] text-[10px] font-bold uppercase tracking-wider">G</th>
              <th className="px-3 py-3 text-center text-[#6b7280] text-[10px] font-bold uppercase tracking-wider">P</th>
              {!isBasketball && <th className="px-3 py-3 text-center text-[#6b7280] text-[10px] font-bold uppercase tracking-wider">E</th>}
              {!isBasketball && <th className="px-3 py-3 text-center text-[#6b7280] text-[10px] font-bold uppercase tracking-wider">GF</th>}
              {!isBasketball && <th className="px-3 py-3 text-center text-[#6b7280] text-[10px] font-bold uppercase tracking-wider">GC</th>}
              {!isBasketball && <th className="px-3 py-3 text-center text-[#6b7280] text-[10px] font-bold uppercase tracking-wider">DG</th>}
              {isBasketball && <th className="px-3 py-3 text-center text-[#6b7280] text-[10px] font-bold uppercase tracking-wider">%</th>}
              <th className="px-3 py-3 text-center text-[#00e87a] text-[10px] font-bold uppercase tracking-wider">
                {isBasketball ? 'Dif' : 'Pts'}
              </th>
              {!isBasketball && <th className="px-3 py-3 text-center text-[#6b7280] text-[10px] font-bold uppercase tracking-wider">Forma</th>}
            </tr>
          </thead>
          <tbody>
            {loading
              ? Array.from({ length: 10 }).map((_, i) => (
                  <tr key={i} className="border-b border-[#1e2028]">
                    <td colSpan={11} className="p-0"><SkeletonRow /></td>
                  </tr>
                ))
              : data.map((team) => {
                  const isTop        = isEuropean ? team.position <= 8 : isBasketball ? team.position <= 8 : team.position <= 4;
                  const isMiddle     = isEuropean ? (team.position >= 9 && team.position <= 24) : (!isBasketball && (team.position === 5 || team.position === 6));
                  const isRelegation = !isEuropean && !isBasketball && team.position >= data.length - 2;
                  return (
                    <tr
                      key={`${team.team}-${team.position}`}
                      className={`border-b border-[#1e2028] hover:bg-white/3 transition-colors ${
                        isTop ? 'border-l-2 border-l-[#00e87a]' : isMiddle ? 'border-l-2 border-l-[#f59e0b]' : isRelegation ? 'border-l-2 border-l-[#ef4444]' : ''
                      }`}
                    >
                      <td className="px-5 py-3">
                        <span className={`text-sm tabular-nums w-5 text-center inline-block ${
                          isTop ? 'text-[#00e87a] font-bold' : isMiddle ? 'text-[#f59e0b] font-bold' : isRelegation ? 'text-[#ef4444] font-bold' : 'text-[#6b7280]'
                        }`}>{team.position}</span>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2.5">
                          <TeamCrest src={team.crest} alt={team.shortName} size={7} />
                          <span className="text-white text-sm font-semibold whitespace-nowrap">{team.team}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-center text-[#a8b2c1] text-sm tabular-nums">{team.played}</td>
                      <td className="px-3 py-3 text-center text-[#a8b2c1] text-sm tabular-nums">{team.won}</td>
                      <td className="px-3 py-3 text-center text-[#a8b2c1] text-sm tabular-nums">{team.lost}</td>
                      {!isBasketball && <td className="px-3 py-3 text-center text-[#a8b2c1] text-sm tabular-nums">{team.draw}</td>}
                      {!isBasketball && <td className="px-3 py-3 text-center text-[#a8b2c1] text-sm tabular-nums">{team.goalsFor}</td>}
                      {!isBasketball && <td className="px-3 py-3 text-center text-[#6b7280] text-sm tabular-nums">{team.goalsAgainst}</td>}
                      {!isBasketball && (
                        <td className="px-3 py-3 text-center text-sm tabular-nums font-semibold">
                          <span className={team.goalDiff > 0 ? 'text-[#00e87a]' : team.goalDiff < 0 ? 'text-[#ef4444]' : 'text-[#6b7280]'}>
                            {team.goalDiff > 0 ? '+' : ''}{team.goalDiff}
                          </span>
                        </td>
                      )}
                      {isBasketball && (
                        <td className="px-3 py-3 text-center text-[#a8b2c1] text-sm tabular-nums">
                          {team.pct !== undefined ? `${(team.pct * 100).toFixed(1)}%` : '—'}
                        </td>
                      )}
                      <td className="px-3 py-3 text-center">
                        <span className="bg-[#00e87a]/15 text-[#00e87a] text-sm font-black px-2 py-0.5 rounded-md tabular-nums">
                          {isBasketball ? (team.goalDiff > 0 ? `+${team.goalDiff}` : team.goalDiff) : team.points}
                        </span>
                      </td>
                      {!isBasketball && (
                        <td className="px-3 py-3">
                          <div className="flex items-center justify-center gap-0.5">
                            {team.form.length > 0
                              ? team.form.map((r, j) => <FormBadge key={j} result={r} />)
                              : <span className="text-[#3a3d4a] text-xs">—</span>}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
          </tbody>
        </table>
      </div>
      <div className="px-5 py-3 border-t border-[#2a2d3a] flex flex-wrap items-center gap-4">
        {isEuropean ? (
          <>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-sm bg-[#00e87a]/20 border-l-2 border-[#00e87a]"></div>
              <span className="text-[#6b7280] text-[10px]">Top 8 — Pasan directo a Octavos</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-sm bg-[#f59e0b]/20 border-l-2 border-[#f59e0b]"></div>
              <span className="text-[#6b7280] text-[10px]">9–24 — Playoff eliminatorio</span>
            </div>
          </>
        ) : isBasketball ? (
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-[#00e87a]/20 border-l-2 border-[#00e87a]"></div>
            <span className="text-[#6b7280] text-[10px]">Top 8 — Playoffs</span>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-sm bg-[#00e87a]/20 border-l-2 border-[#00e87a]"></div>
              <span className="text-[#6b7280] text-[10px]">Champions League</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-sm bg-[#f59e0b]/20 border-l-2 border-[#f59e0b]"></div>
              <span className="text-[#6b7280] text-[10px]">Europa / Conference</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-sm bg-[#ef4444]/20 border-l-2 border-[#ef4444]"></div>
              <span className="text-[#6b7280] text-[10px]">Descenso</span>
            </div>
          </>
        )}
        <div className="ml-auto flex items-center gap-1.5">
          <i className="ri-information-line text-[#6b7280] text-xs"></i>
          <span className="text-[#6b7280] text-[10px]">Datos en tiempo real vía ESPN</span>
        </div>
      </div>
    </div>
  );
}

// ─── Knockout matches ─────────────────────────────────────────────────────────
function KnockoutView({ espnId }: { espnId: string }) {
  const [matches, setMatches] = useState<KnockoutMatch[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchKnockout = useCallback(async () => {
    setLoading(true);
    try {
      const [r2, r3] = await Promise.all([
        fetch(`https://site.api.espn.com/apis/site/v2/sports/soccer/${espnId}/scoreboard?seasontype=2&limit=100`),
        fetch(`https://site.api.espn.com/apis/site/v2/sports/soccer/${espnId}/scoreboard?seasontype=3&limit=100`),
      ]);
      const [d2, d3] = await Promise.all([r2.ok ? r2.json() : { events: [] }, r3.ok ? r3.json() : { events: [] }]);
      const all = [...(d2.events ?? []), ...(d3.events ?? [])];
      const seen = new Set<string>();
      const unique = all.filter((e: Record<string, unknown>) => { const id = String(e.id); if (seen.has(id)) return false; seen.add(id); return true; });

      const parsed: KnockoutMatch[] = unique.map((event: Record<string, unknown>) => {
        const comp = (event.competitions as Record<string, unknown>[])?.[0] ?? {};
        const competitors = (comp.competitors as Record<string, unknown>[]) ?? [];
        const home = competitors.find((c) => (c as { homeAway: string }).homeAway === 'home') as Record<string, unknown> | undefined;
        const away = competitors.find((c) => (c as { homeAway: string }).homeAway === 'away') as Record<string, unknown> | undefined;
        const homeTeam = (home?.team as Record<string, unknown>) ?? {};
        const awayTeam = (away?.team as Record<string, unknown>) ?? {};
        const statusType = ((comp.status as Record<string, unknown>)?.type as Record<string, unknown>) ?? {};
        const state = (statusType.state as string) ?? 'pre';
        const notes = (comp.notes as Record<string, unknown>[]) ?? [];
        const round = (notes[0]?.headline ?? notes[0]?.value ?? '') as string;
        const venue = (comp.venue as Record<string, unknown>) ?? {};
        const eventDate = new Date((event.date as string) ?? '');
        const isValid = !isNaN(eventDate.getTime());
        return {
          id: event.id as string,
          homeTeam: (homeTeam.displayName as string) ?? 'TBD',
          awayTeam: (awayTeam.displayName as string) ?? 'TBD',
          homeCrest: ((homeTeam.logos as Record<string, unknown>[])?.[0]?.href as string) ?? '',
          awayCrest: ((awayTeam.logos as Record<string, unknown>[])?.[0]?.href as string) ?? '',
          homeScore: (home?.score as string) ?? '—',
          awayScore: (away?.score as string) ?? '—',
          date: isValid ? (event.date as string).split('T')[0] : '',
          time: isValid ? eventDate.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Madrid' }) : '—',
          status: state as 'pre' | 'in' | 'post',
          round,
          venue: ((venue.fullName as string) ?? (venue.address as Record<string, unknown>)?.city as string) ?? '',
        };
      });

      parsed.sort((a, b) => { const o: Record<string, number> = { in: 0, pre: 1, post: 2 }; return (o[a.status] ?? 1) - (o[b.status] ?? 1); });
      setMatches(parsed);
    } catch { setMatches([]); }
    finally { setLoading(false); }
  }, [espnId]);

  useEffect(() => { fetchKnockout(); }, [fetchKnockout]);

  if (loading) return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {Array.from({ length: 8 }).map((_, i) => <div key={i} className="bg-[#151719] border border-[#2a2d3a] rounded-xl p-4 animate-pulse h-28"></div>)}
    </div>
  );

  if (matches.length === 0) return (
    <div className="bg-[#151719] border border-[#2a2d3a] rounded-xl p-12 text-center">
      <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-white/5 mx-auto mb-3">
        <i className="ri-trophy-line text-[#6b7280] text-2xl"></i>
      </div>
      <p className="text-white font-semibold text-sm">Sin partidos disponibles</p>
      <p className="text-[#6b7280] text-xs mt-1">Los partidos de eliminatorias aún no están disponibles.</p>
    </div>
  );

  const grouped: Record<string, KnockoutMatch[]> = {};
  for (const m of matches) { const key = m.round || 'Eliminatorias'; if (!grouped[key]) grouped[key] = []; grouped[key].push(m); }

  return (
    <div className="space-y-6">
      {Object.entries(grouped).map(([roundLabel, roundMatches]) => (
        <div key={roundLabel}>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-5 h-5 flex items-center justify-center text-[#ffd700]"><i className="ri-trophy-line text-sm"></i></div>
            <h4 className="text-white font-bold text-sm">{roundLabel}</h4>
            <div className="flex-1 h-px bg-[#2a2d3a]"></div>
            <span className="text-[#6b7280] text-xs">{roundMatches.length} partidos</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {roundMatches.map((m) => {
              const isLive = m.status === 'in'; const isFinished = m.status === 'post';
              const dateObj = m.date ? new Date(m.date) : null;
              const dayStr = dateObj ? dateObj.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' }) : '—';
              return (
                <div key={m.id} className="bg-[#151719] border border-[#2a2d3a] rounded-xl p-4 hover:border-[#ffd700]/20 transition-colors">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[#6b7280] text-[10px] font-bold uppercase tracking-wider">{m.round || 'Eliminatoria'}</span>
                    <span className={`text-[10px] font-bold flex items-center gap-1 ${isLive ? 'text-[#ef4444]' : isFinished ? 'text-[#a8b2c1]' : 'text-[#ffd700]'}`}>
                      {isLive && <span className="w-1.5 h-1.5 rounded-full bg-[#ef4444] animate-pulse inline-block"></span>}
                      {isLive ? 'EN VIVO' : isFinished ? dayStr : `${dayStr} · ${m.time}`}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 flex items-center gap-2.5 min-w-0">
                      <TeamCrest src={m.homeCrest} alt={m.homeTeam} size={9} />
                      <span className="text-white text-sm font-semibold truncate">{m.homeTeam}</span>
                    </div>
                    <div className="flex-shrink-0 text-center px-3">
                      {isFinished || isLive ? (
                        <div className="flex items-center gap-1">
                          <span className={`text-xl font-black tabular-nums ${isFinished && parseInt(m.homeScore) > parseInt(m.awayScore) ? 'text-[#00e87a]' : 'text-white'}`}>{m.homeScore}</span>
                          <span className="text-[#3d4149] font-black text-sm">—</span>
                          <span className={`text-xl font-black tabular-nums ${isFinished && parseInt(m.awayScore) > parseInt(m.homeScore) ? 'text-[#00e87a]' : 'text-white'}`}>{m.awayScore}</span>
                        </div>
                      ) : <span className="text-[#3d4149] font-black text-sm tracking-widest">vs</span>}
                    </div>
                    <div className="flex-1 flex items-center gap-2.5 min-w-0 justify-end">
                      <span className="text-white text-sm font-semibold truncate text-right">{m.awayTeam}</span>
                      <TeamCrest src={m.awayCrest} alt={m.awayTeam} size={9} />
                    </div>
                  </div>
                  {m.venue && <div className="mt-3 flex items-center gap-1.5 justify-center"><i className="ri-map-pin-2-line text-[#6b7280] text-xs"></i><span className="text-[#6b7280] text-[10px] truncate">{m.venue}</span></div>}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Upcoming match card ──────────────────────────────────────────────────────
function UpcomingMatchCard({ match }: { match: UpcomingMatch }) {
  const dateObj = match.date ? new Date(`${match.date}T12:00:00`) : null;
  const day = dateObj ? dateObj.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' }) : '—';
  const isLive = match.status === 'in'; const isFinished = match.status === 'post';
  return (
    <div className="bg-[#151719] border border-[#2a2d3a] rounded-xl p-4 hover:border-[#00e87a]/30 transition-colors">
      <div className="flex items-center justify-between mb-3">
        <span className={`text-[10px] font-bold flex items-center gap-1 ${isLive ? 'text-[#ef4444]' : isFinished ? 'text-[#a8b2c1]' : 'text-[#ffd700]'}`}>
          {isLive && <span className="w-1.5 h-1.5 rounded-full bg-[#ef4444] animate-pulse inline-block"></span>}
          {isLive ? 'EN VIVO' : isFinished ? 'Finalizado' : `${day} · ${match.time}`}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex-1 flex flex-col items-center gap-2">
          <TeamCrest src={match.homeCrest} alt={match.homeTeam} size={10} />
          <p className="text-white text-xs font-semibold text-center leading-tight">{match.homeTeam}</p>
        </div>
        <div className="flex flex-col items-center gap-1 px-2">
          {(isLive || isFinished) ? (
            <div className="flex items-center gap-1">
              <span className="text-white text-lg font-black tabular-nums">{match.homeScore}</span>
              <span className="text-[#3d4149] font-black">—</span>
              <span className="text-white text-lg font-black tabular-nums">{match.awayScore}</span>
            </div>
          ) : <span className="text-[#6b7280] text-xs font-black">VS</span>}
        </div>
        <div className="flex-1 flex flex-col items-center gap-2">
          <TeamCrest src={match.awayCrest} alt={match.awayTeam} size={10} />
          <p className="text-white text-xs font-semibold text-center leading-tight">{match.awayTeam}</p>
        </div>
      </div>
      {match.venue && <div className="mt-3 flex items-center gap-1.5 justify-center"><i className="ri-map-pin-2-line text-[#6b7280] text-xs"></i><span className="text-[#6b7280] text-[10px] truncate">{match.venue}</span></div>}
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────
export default function ClasificacionPage() {
  const [selectedComp, setSelectedComp] = useState<CompId>('laliga');
  const [standings, setStandings]       = useState<StandingTeam[]>([]);
  const [matches, setMatches]           = useState<UpcomingMatch[]>([]);
  const [oddsEvents, setOddsEvents]     = useState<OddsEvent[]>([]);
  const [loading, setLoading]           = useState(true);
  const [oddsLoading, setOddsLoading]   = useState(false);
  const [error, setError]               = useState<string | null>(null);
  const [activeTab, setActiveTab]       = useState<'standings' | 'matches' | 'odds'>('standings');
  const [lastUpdated, setLastUpdated]   = useState('');

  const comp = COMPETITIONS.find((c) => c.id === selectedComp)!;
  const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;

  // ── Fetch odds from The Odds API via edge function ──────────────────────────
  const fetchOdds = useCallback(async (compId: CompId) => {
    setOddsLoading(true);
    setOddsEvents([]);
    try {
      const res = await fetch(`${supabaseUrl}/functions/v1/odds-comparador?sport=${compId}`);
      const data = await res.json();
      if (data.status === 'ok' && Array.isArray(data.data)) {
        setOddsEvents(data.data as OddsEvent[]);
      }
    } catch { setOddsEvents([]); }
    finally { setOddsLoading(false); }
  }, [supabaseUrl]);

  // ── Fetch ESPN standings + scoreboard ───────────────────────────────────────
  const fetchData = useCallback(async (compId: CompId) => {
    const c = COMPETITIONS.find((x) => x.id === compId)!;
    setLoading(true);
    setError(null);
    setStandings([]);
    setMatches([]);

    try {
      const sportPath = c.sport === 'basketball' ? 'basketball' : 'soccer';

      if (c.sport === 'basketball') {
        const [standRes, scoreRes] = await Promise.all([
          fetch(`https://site.api.espn.com/apis/v2/sports/basketball/nba/standings`),
          fetch(`https://site.api.espn.com/apis/site/v2/sports/basketball/nba/scoreboard?limit=20`),
        ]);
        const standJson = standRes.ok ? await standRes.json() : {};
        const scoreJson = scoreRes.ok ? await scoreRes.json() : { events: [] };
        const rawEntries: Record<string, unknown>[] =
          standJson.standings?.[0]?.entries ?? standJson.children?.[0]?.standings?.entries ?? standJson.standings?.entries ?? [];
        const transformed: StandingTeam[] = rawEntries.map((entry: Record<string, unknown>, idx: number) => {
          const stats = (entry.stats as Record<string, unknown>[]) ?? [];
          const wins = getStat(stats, 'wins'); const losses = getStat(stats, 'losses');
          const played = wins + losses;
          const pct = getStat(stats, 'winPercent') || getStat(stats, 'pct') || (played > 0 ? wins / played : 0);
          const diff = getStat(stats, 'pointDifferential') || getStat(stats, 'avgPointDifferential') || 0;
          const team = (entry.team as Record<string, unknown>) ?? {};
          return { position: idx + 1, team: (team.displayName as string) ?? 'Desconocido', shortName: (team.abbreviation as string) ?? '???', crest: ((team.logos as Record<string, unknown>[])?.[0]?.href as string) ?? '', played, won: wins, draw: 0, lost: losses, goalsFor: 0, goalsAgainst: 0, goalDiff: Math.round(diff), points: wins, form: [], pct };
        });
        transformed.sort((a, b) => b.won - a.won || (b.pct ?? 0) - (a.pct ?? 0));
        transformed.forEach((t, i) => { t.position = i + 1; });
        setStandings(transformed);
        const events: Record<string, unknown>[] = (scoreJson.events ?? []).slice(0, 12);
        setMatches(events.map((event: Record<string, unknown>) => {
          const comp2 = (event.competitions as Record<string, unknown>[])?.[0] ?? {};
          const competitors = (comp2.competitors as Record<string, unknown>[]) ?? [];
          const home = competitors.find((c2) => (c2 as { homeAway: string }).homeAway === 'home') as Record<string, unknown> | undefined;
          const away = competitors.find((c2) => (c2 as { homeAway: string }).homeAway === 'away') as Record<string, unknown> | undefined;
          const homeTeam = (home?.team as Record<string, unknown>) ?? {}; const awayTeam = (away?.team as Record<string, unknown>) ?? {};
          const state = (((comp2.status as Record<string, unknown>)?.type as Record<string, unknown>)?.state as string) ?? 'pre';
          const venue = (comp2.venue as Record<string, unknown>) ?? {};
          const eventDate = new Date((event.date as string) ?? ''); const isValid = !isNaN(eventDate.getTime());
          return { id: event.id as string, homeTeam: (homeTeam.displayName as string) ?? 'TBD', awayTeam: (awayTeam.displayName as string) ?? 'TBD', homeCrest: ((homeTeam.logos as Record<string, unknown>[])?.[0]?.href as string) ?? '', awayCrest: ((awayTeam.logos as Record<string, unknown>[])?.[0]?.href as string) ?? '', date: isValid ? (event.date as string).split('T')[0] : '', time: isValid ? eventDate.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: 'America/New_York' }) : '—', venue: ((venue.fullName as string) ?? (venue.address as Record<string, unknown>)?.city as string) ?? '', status: state as 'pre' | 'in' | 'post', homeScore: (home?.score as string) ?? '—', awayScore: (away?.score as string) ?? '—' };
        }));
      } else {
        const [standRes, scoreRes] = await Promise.all([
          fetch(`https://site.api.espn.com/apis/v2/sports/${sportPath}/${c.espnId}/standings`),
          fetch(`https://site.api.espn.com/apis/site/v2/sports/${sportPath}/${c.espnId}/scoreboard?limit=20`),
        ]);
        if (!standRes.ok) throw new Error(`ESPN no devolvió datos para ${c.label}`);
        const standJson = await standRes.json();
        const scoreJson = scoreRes.ok ? await scoreRes.json() : { events: [] };
        const rawEntries: Record<string, unknown>[] =
          standJson.standings?.[0]?.entries ?? standJson.children?.[0]?.standings?.entries ?? standJson.standings?.entries ?? [];
        const transformed: StandingTeam[] = rawEntries.map((entry: Record<string, unknown>, idx: number) => {
          const stats = (entry.stats as Record<string, unknown>[]) ?? [];
          const formStat = stats.find((s) => s.name === 'form' || s.name === 'streak' || s.name === 'lastTenResults');
          const formStr = (formStat?.displayValue ?? formStat?.value ?? '') as string;
          const team = (entry.team as Record<string, unknown>) ?? {};
          return { position: getStat(stats, 'rank') || (idx + 1), team: (team.displayName as string) ?? 'Desconocido', shortName: (team.abbreviation as string) ?? '???', crest: ((team.logos as Record<string, unknown>[])?.[0]?.href as string) ?? '', played: getStat(stats, 'gamesPlayed'), won: getStat(stats, 'wins'), draw: getStat(stats, 'ties'), lost: getStat(stats, 'losses'), goalsFor: getStat(stats, 'pointsFor'), goalsAgainst: getStat(stats, 'pointsAgainst'), goalDiff: getStat(stats, 'pointDifferential'), points: getStat(stats, 'points'), form: parseForm(formStr) };
        });
        transformed.sort((a, b) => b.points - a.points || b.goalDiff - a.goalDiff || b.goalsFor - a.goalsFor);
        transformed.forEach((t, i) => { t.position = i + 1; });
        setStandings(transformed);
        const events: Record<string, unknown>[] = (scoreJson.events ?? []).slice(0, 12);
        setMatches(events.map((event: Record<string, unknown>) => {
          const comp2 = (event.competitions as Record<string, unknown>[])?.[0] ?? {};
          const competitors = (comp2.competitors as Record<string, unknown>[]) ?? [];
          const home = competitors.find((c2) => (c2 as { homeAway: string }).homeAway === 'home') as Record<string, unknown> | undefined;
          const away = competitors.find((c2) => (c2 as { homeAway: string }).homeAway === 'away') as Record<string, unknown> | undefined;
          const homeTeam = (home?.team as Record<string, unknown>) ?? {}; const awayTeam = (away?.team as Record<string, unknown>) ?? {};
          const state = (((comp2.status as Record<string, unknown>)?.type as Record<string, unknown>)?.state as string) ?? 'pre';
          const venue = (comp2.venue as Record<string, unknown>) ?? {};
          const eventDate = new Date((event.date as string) ?? ''); const isValid = !isNaN(eventDate.getTime());
          return { id: event.id as string, homeTeam: (homeTeam.displayName as string) ?? 'TBD', awayTeam: (awayTeam.displayName as string) ?? 'TBD', homeCrest: ((homeTeam.logos as Record<string, unknown>[])?.[0]?.href as string) ?? '', awayCrest: ((awayTeam.logos as Record<string, unknown>[])?.[0]?.href as string) ?? '', date: isValid ? (event.date as string).split('T')[0] : '', time: isValid ? eventDate.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Madrid' }) : '—', venue: ((venue.fullName as string) ?? (venue.address as Record<string, unknown>)?.city as string) ?? '', status: state as 'pre' | 'in' | 'post', homeScore: (home?.score as string) ?? '—', awayScore: (away?.score as string) ?? '—' };
        }));
      }
      setLastUpdated(new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(selectedComp);
    fetchOdds(selectedComp);
    setActiveTab('standings');
  }, [selectedComp, fetchData, fetchOdds]);

  useEffect(() => {
    document.title = 'Clasificación en Tiempo Real — Champions, Europa, Conference, NBA | Apuestamente';
  }, []);

  const currentYear = new Date().getFullYear();
  const season = `${currentYear - 1}/${String(currentYear).slice(-2)}`;

  return (
    <div className="bg-[#0d0f14] min-h-screen">
      <Navbar />
      <div className="pt-16">

        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className="bg-[#151719] border-b border-[#2a2d3a]">
          <div className="max-w-[1440px] mx-auto px-6 py-10">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
              <div>
                <p className="text-[#00e87a] text-xs font-bold tracking-widest uppercase mb-2 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00e87a] animate-pulse inline-block"></span>
                  ESPN + The Odds API · Tiempo Real
                </p>
                <h1 className="text-white text-4xl font-black uppercase">Clasificación</h1>
                <p className="text-[#a8b2c1] text-sm mt-2">
                  Champions · Europa · Conference · NBA · Grandes Ligas · Temporada{' '}
                  <strong className="text-white">{season}</strong>
                </p>
              </div>
              <div className="flex items-center gap-3">
                {lastUpdated && !loading && (
                  <span className="text-[#6b7280] text-xs flex items-center gap-1">
                    <i className="ri-time-line text-xs"></i>
                    {lastUpdated}
                  </span>
                )}
                <button
                  onClick={() => { fetchData(selectedComp); fetchOdds(selectedComp); }}
                  disabled={loading}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#1e2028] border border-[#2a2d3a] text-[#a8b2c1] text-xs font-semibold hover:text-white transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap"
                >
                  <i className={`ri-refresh-line text-xs ${loading ? 'animate-spin' : ''}`}></i>
                  Actualizar
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-[1440px] mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">

            {/* ── Sidebar ─────────────────────────────────────────────── */}
            <div className="lg:col-span-1 space-y-4">
              <div className="bg-[#151719] border border-[#2a2d3a] rounded-xl p-4">
                {GROUPS.map((group) => {
                  const groupComps = COMPETITIONS.filter((c) => c.group === group.id);
                  return (
                    <div key={group.id} className="mb-4 last:mb-0">
                      <div className="flex items-center gap-1.5 mb-2">
                        <div className="w-4 h-4 flex items-center justify-center text-[#6b7280]">
                          <i className={`${group.icon} text-xs`}></i>
                        </div>
                        <p className="text-[#6b7280] text-[10px] font-bold uppercase tracking-widest">{group.label}</p>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        {groupComps.map((c) => (
                          <button
                            key={c.id}
                            onClick={() => setSelectedComp(c.id)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer text-left ${
                              selectedComp === c.id
                                ? 'bg-[#00e87a]/15 text-[#00e87a] border border-[#00e87a]/20'
                                : 'text-[#a8b2c1] hover:text-white hover:bg-white/5'
                            }`}
                          >
                            <span className="text-base flex-shrink-0">{c.flag}</span>
                            <div className="min-w-0 flex-1">
                              <p className="font-semibold text-xs truncate">{c.label}</p>
                              <p className="text-[9px] text-[#6b7280]">{c.country}</p>
                            </div>
                            {selectedComp === c.id && (
                              <div className="w-3 h-3 flex items-center justify-center text-[#00e87a] flex-shrink-0">
                                <i className="ri-check-line text-xs"></i>
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Leader card */}
              {!loading && standings.length > 0 && (
                <div className="bg-[#151719] border border-[#2a2d3a] rounded-xl p-4 space-y-3">
                  <p className="text-[#6b7280] text-xs font-bold uppercase tracking-widest">
                    {comp.isKnockout ? 'Primero de la Fase' : comp.sport === 'basketball' ? 'Mejor Récord' : 'Líder'}
                  </p>
                  <div className="flex items-center gap-2.5">
                    <TeamCrest src={standings[0].crest} alt={standings[0].shortName} size={9} />
                    <div>
                      <p className="text-white text-sm font-bold">{standings[0].team}</p>
                      <p className="text-[#00e87a] text-xs font-bold">
                        {comp.sport === 'basketball' ? `${standings[0].won}V - ${standings[0].lost}D` : `${standings[0].points} puntos`}
                      </p>
                    </div>
                  </div>
                  {!comp.isKnockout && (() => {
                    const stats = comp.sport === 'basketball'
                      ? [{ label: 'V', val: standings[0].won, color: '#00e87a' }, { label: 'D', val: standings[0].lost, color: '#ef4444' }, { label: '%', val: `${((standings[0].pct ?? 0) * 100).toFixed(0)}%`, color: '#ffd700' }]
                      : [{ label: 'G', val: standings[0].won, color: '#00e87a' }, { label: 'E', val: standings[0].draw, color: '#f59e0b' }, { label: 'P', val: standings[0].lost, color: '#ef4444' }];
                    return (
                      <div className="grid grid-cols-3 gap-2 pt-2 border-t border-[#2a2d3a]">
                        {stats.map((s) => (
                          <div key={s.label} className="text-center">
                            <p className="text-lg font-black" style={{ color: s.color }}>{s.val}</p>
                            <p className="text-[#6b7280] text-[10px]">{s.label}</p>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Odds summary in sidebar */}
              {!oddsLoading && oddsEvents.length > 0 && (
                <div className="bg-[#151719] border border-[#ffd700]/20 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-5 h-5 flex items-center justify-center text-[#ffd700]">
                      <i className="ri-exchange-dollar-line text-sm"></i>
                    </div>
                    <p className="text-[#ffd700] text-xs font-bold uppercase tracking-widest">Cuotas reales</p>
                  </div>
                  <p className="text-[#a8b2c1] text-xs mb-3">
                    {oddsEvents.length} partido{oddsEvents.length !== 1 ? 's' : ''} con cuotas disponibles
                  </p>
                  <button
                    onClick={() => setActiveTab('odds')}
                    className="w-full py-2 rounded-xl bg-[#ffd700]/10 border border-[#ffd700]/20 text-[#ffd700] text-xs font-bold cursor-pointer hover:bg-[#ffd700]/20 transition-all whitespace-nowrap"
                  >
                    Ver cuotas reales
                  </button>
                </div>
              )}
            </div>

            {/* ── Main content ─────────────────────────────────────────── */}
            <div className="lg:col-span-3 space-y-4">

              {/* Tabs */}
              <div className="flex items-center gap-1 bg-[#151719] border border-[#2a2d3a] rounded-xl p-1 w-fit flex-wrap">
                {[
                  { id: 'standings', label: comp.isKnockout ? 'Eliminatorias' : 'Clasificación', icon: 'ri-bar-chart-horizontal-line' },
                  { id: 'matches',   label: `Partidos${matches.length > 0 ? ` (${matches.length})` : ''}`, icon: 'ri-calendar-event-line' },
                  { id: 'odds',      label: `Cuotas Reales${oddsEvents.length > 0 ? ` (${oddsEvents.length})` : ''}`, icon: 'ri-exchange-dollar-line' },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as 'standings' | 'matches' | 'odds')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                      activeTab === tab.id
                        ? tab.id === 'odds' ? 'bg-[#ffd700]/15 text-[#ffd700]' : 'bg-[#00e87a]/15 text-[#00e87a]'
                        : 'text-[#6b7280] hover:text-white'
                    }`}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${tab.icon} text-sm`}></i>
                    </div>
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Error */}
              {error && (
                <div className="bg-[#ef4444]/10 border border-[#ef4444]/30 rounded-xl px-5 py-4 flex items-start gap-3">
                  <i className="ri-error-warning-line text-[#ef4444] text-base mt-0.5 flex-shrink-0"></i>
                  <div>
                    <p className="text-[#ef4444] font-semibold text-sm">Error al cargar datos</p>
                    <p className="text-[#a8b2c1] text-xs mt-1">{error}</p>
                    <button onClick={() => fetchData(selectedComp)} className="mt-2 text-[#ef4444] text-xs underline cursor-pointer whitespace-nowrap">Reintentar</button>
                  </div>
                </div>
              )}

              {/* Competition info banner */}
              {!error && activeTab !== 'odds' && (
                <div className="bg-[#00e87a]/5 border border-[#00e87a]/20 rounded-xl px-5 py-3 flex items-center gap-3">
                  <span className="text-2xl">{comp.flag}</span>
                  <div>
                    <p className="text-white font-bold text-sm">{comp.label} — {comp.country}</p>
                    <p className="text-[#6b7280] text-xs">
                      Temporada {season} · {loading ? 'Cargando...' : `${standings.length} equipos · ESPN API`}
                    </p>
                  </div>
                  <div className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#00e87a]/10 border border-[#00e87a]/20 text-[#00e87a] text-xs font-bold flex-shrink-0">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#00e87a] animate-pulse inline-block"></span>
                    En vivo
                  </div>
                </div>
              )}

              {/* Odds banner */}
              {activeTab === 'odds' && (
                <div className="bg-[#ffd700]/5 border border-[#ffd700]/20 rounded-xl px-5 py-3 flex items-center gap-3">
                  <span className="text-2xl">{comp.flag}</span>
                  <div>
                    <p className="text-white font-bold text-sm">{comp.label} — Cuotas Reales</p>
                    <p className="text-[#6b7280] text-xs">
                      {oddsLoading ? 'Cargando cuotas...' : `${oddsEvents.length} partidos · The Odds API`}
                    </p>
                  </div>
                  <div className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#ffd700]/10 border border-[#ffd700]/20 text-[#ffd700] text-xs font-bold flex-shrink-0">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#ffd700] animate-pulse inline-block"></span>
                    Cuotas reales
                  </div>
                </div>
              )}

              {/* ── Standings / Knockout ── */}
              {activeTab === 'standings' && (
                comp.isKnockout
                  ? <KnockoutView key={comp.id} espnId={comp.espnId} />
                  : <StandingsTable data={standings} loading={loading} isEuropean={false} isBasketball={comp.sport === 'basketball'} />
              )}

              {/* ── Matches ── */}
              {activeTab === 'matches' && (
                loading ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Array.from({ length: 6 }).map((_, i) => <div key={i} className="bg-[#151719] border border-[#2a2d3a] rounded-xl p-4 animate-pulse h-40"></div>)}
                  </div>
                ) : matches.length === 0 ? (
                  <div className="bg-[#151719] border border-[#2a2d3a] rounded-xl p-10 text-center">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-white/5 mx-auto mb-3">
                      <i className="ri-calendar-2-line text-[#6b7280] text-2xl"></i>
                    </div>
                    <p className="text-white font-semibold text-sm">Sin partidos disponibles</p>
                    <p className="text-[#6b7280] text-xs mt-1">No hay partidos para {comp.label} en este momento.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {matches.map((m) => <UpcomingMatchCard key={m.id} match={m} />)}
                  </div>
                )
              )}

              {/* ── Cuotas Reales ── */}
              {activeTab === 'odds' && (
                oddsLoading ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {Array.from({ length: 6 }).map((_, i) => <div key={i} className="bg-[#151719] border border-[#2a2d3a] rounded-xl p-4 animate-pulse h-52"></div>)}
                  </div>
                ) : oddsEvents.length === 0 ? (
                  <div className="bg-[#151719] border border-[#2a2d3a] rounded-xl p-12 text-center">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-white/5 mx-auto mb-3">
                      <i className="ri-exchange-dollar-line text-[#6b7280] text-2xl"></i>
                    </div>
                    <p className="text-white font-semibold text-sm">Sin cuotas disponibles</p>
                    <p className="text-[#6b7280] text-xs mt-1">
                      No hay cuotas disponibles para {comp.label} en este momento.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {oddsEvents.map((ev) => (
                      <OddsMatchCard key={ev.id} event={ev} isSoccer={comp.sport === 'soccer'} />
                    ))}
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
